import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d918qmk6ci2plld1okfg';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setStatus('success');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 4000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const topCategories = categories.slice(0, 4);

  return (
    <footer className="mx-4 md:mx-8 lg:mx-12 mb-6 md:mb-8 rounded-2xl bg-primary-500 overflow-hidden">
      <div className="px-8 md:px-12 lg:px-16 py-14 md:py-18">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Newsletter - Left */}
          <div className="md:col-span-5 lg:col-span-5">
            <h3 className="text-2xl md:text-3xl font-light text-background-50 font-heading tracking-tight">
              Stay Updated
            </h3>
            <p className="mt-3 text-background-50/60 text-sm leading-relaxed max-w-sm">
              Get the latest pandabuy spreadsheet updates, new curated lists, and sourcing tips delivered to your inbox weekly.
            </p>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <div className="visually-hidden-field" aria-hidden="true">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
              </div>
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-transparent border-b border-background-50/30 px-2 py-3 text-background-50 text-sm placeholder:text-background-50/40 focus:outline-none focus:border-background-50/70 transition-colors duration-200"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="whitespace-nowrap px-6 py-3 bg-background-50 text-primary-500 text-sm font-semibold rounded-full hover:bg-background-100 transition-all duration-200 disabled:opacity-60 cursor-pointer"
              >
                {status === 'loading' ? 'Joining...' : 'Join Now →'}
              </button>
            </form>
            {status === 'success' && (
              <p className="mt-3 text-background-50/80 text-sm">Thanks for subscribing! Check your inbox soon.</p>
            )}
            {status === 'error' && (
              <p className="mt-3 text-accent-300 text-sm">Something went wrong. Please try again.</p>
            )}
          </div>

          {/* Categories - Middle */}
          <div className="md:col-span-3 lg:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/50 mb-5">
              Categories
            </h4>
            <div className="flex flex-col gap-3">
              {topCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-background-50/70 hover:text-background-50 text-sm transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
                >
                  {cat.name}
                </a>
              ))}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-accent-300 hover:text-accent-200 text-sm font-medium transition-colors duration-200 mt-1"
              >
                View All Categories →
              </a>
            </div>
          </div>

          {/* Contact - Right */}
          <div className="md:col-span-4 lg:col-span-4">
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/50 mb-5">
              Contact
            </h4>
            <div className="space-y-3 text-background-50/70 text-sm">
              <p>
                <a
                  href="mailto:hello@pandabuy.sbs"
                  className="hover:text-background-50 transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
                >
                  hello@pandabuy.sbs
                </a>
              </p>
              <p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="hover:text-background-50 transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
                >
                  kaspreadsheet.com
                </a>
              </p>
            </div>

            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/50 mt-8 mb-4">
              Quick Links
            </h4>
            <div className="space-y-2.5 text-background-50/70 text-sm">
              <p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="hover:text-background-50 transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
                >
                  Pandabuy Sheet (Main Site)
                </a>
              </p>
              <p>
                <Link
                  to="/pandabuy-sheet-guide"
                  className="hover:text-background-50 transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
                >
                  Spreadsheet Guide
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-background-50/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="text-background-50/50 hover:text-background-50 text-sm transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
            >
              Instagram
            </a>
            <a
              href="#"
              className="text-background-50/50 hover:text-background-50 text-sm transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
            >
              Twitter
            </a>
            <a
              href="#"
              className="text-background-50/50 hover:text-background-50 text-sm transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
            >
              Discord
            </a>
          </div>

          <p className="text-background-50/40 text-sm order-first sm:order-none">
            &copy; {new Date().getFullYear()} pandabuy.sbs. All rights reserved.
          </p>

          <div className="flex items-center gap-5">
            <a
              href="#"
              className="text-background-50/50 hover:text-background-50 text-sm transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-background-50/50 hover:text-background-50 text-sm transition-colors duration-200 underline decoration-background-50/20 hover:decoration-background-50/50 underline-offset-4"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}